import React, { useState, useEffect } from "react";
import axios from "axios";
import { observer } from "mobx-react-lite";
import { globalVars } from "../../global/GlobalVars.js";
import * as language from "../../utils/language.js";
import * as navigation from "../../utils/navigation.js";
import * as websocket from "../../utils/communication.js";
import * as XLSX from "xlsx";

//========================================================================
// Uhrzeit formattieren
export const formatDate = (dateString) => {
  const options = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  };
  if (dateString === "") {
    return "";
  } else {
    return new Date(dateString).toLocaleDateString("de-DE", options);
  }
};

//========================================================================
// Konfiguration der api-Schnittstelle
const alarmUrl =
  window.location.port === "3000"
    ? `http://${window.location.hostname}:${globalVars.httpPort}/alarms/`
    : `/api/alarms/`;

//========================================================================
// nächste Alarme nachladen
const fetchNextArchivedAlarms = async (offset = 200) => {
  try {
    const response = await axios.get(alarmUrl + "nextArchivedAlarms", {
      params: {
        offset: offset,
      },
    });

    if (response.data.success) {
      return {
        alarms: response.data.alarms,
        nextOffset: response.data.nextOffset,
        hasMoreArchivedAlarms: response.data.hasMoreArchivedAlarms,
        error: null,
      };
    } else {
      return {
        alarms: [],
        nextOffset: offset,
        hasMoreArchivedAlarms: false,
        error: response.data.message || "Failed to fetch archived alarms",
      };
    }
  } catch (error) {
    return {
      alarms: [],
      nextOffset: offset,
      hasMoreArchivedAlarms: false,
      error:
        error.response?.data?.message ||
        "Network error while fetching archived alarms",
    };
  }
};

//========================================================================
// Tabelle
const TableComponentMobile = observer(
  ({ style, scaleFactor, alarms: initialAlarms, activeAlarms }) => {
    const [sortConfig, setSortConfig] = useState({
      key: null,
      direction: "ascending",
    });
    const [selectedRows, setSelectedRows] = useState([]);
    const [archivedAlarmsOffset, setArchivedAlarmsOffset] = useState(200);
    const [hasMoreArchivedAlarms, setHasMoreArchivedAlarms] = useState(true);
    const [archivedAlarms, setArchivedAlarms] = useState(initialAlarms);

    // Aktualisiere archivierte Alarme wenn sich initialAlarms ändert
    useEffect(() => {
      if (!activeAlarms) {
        setArchivedAlarms(initialAlarms);
        setArchivedAlarmsOffset(200);
        setHasMoreArchivedAlarms(true);
      }
    }, [initialAlarms, activeAlarms]);

    //========================================================================
    // Alarmtyp übersetzen
    const alarmSeverity = (severity) => {
      if (severity === "alarm") {
        return language.displayText(["Alarm", "errore", "alarm"]);
      } else if (severity === "fault") {
        return language.displayText(["Störung", "blocco", "fault"]);
      } else if (severity === "warning") {
        return language.displayText(["Warnung", "avvertimento", "warning"]);
      }
    };

    //========================================================================
    // Status des Alarms
    const alarmState = (state) => {
      if (state === 0) {
        return language.displayText([
          "Alarm nicht aktiv und quittiert",
          "allarme non attivo e confermato",
          "alarm not active and acknowledged",
        ]);
      } else if (state === 1) {
        return language.displayText([
          "Alarm aktiv",
          "allarme attivo",
          "alarm active",
        ]);
      } else if (state === 2) {
        return language.displayText([
          "Alarm aktiv und quittiert",
          "allarme attivo e confermato",
          "alarm active and acknowledged",
        ]);
      } else if (state === 3) {
        return language.displayText([
          "Alarm nicht aktiv",
          "allarme non attivo",
          "alarm not active",
        ]);
      }
    };

    //========================================================================
    // Zeilenfarbe je nach Typ und Status
    const getRowColor = (severity, state, isSelected) => {
      let backgroundColor;
      if (severity === "alarm") {
        backgroundColor = state === 1 || state === 3 ? "#ff0000" : "#ffaaaa";
      } else if (severity === "fault") {
        backgroundColor = state === 1 || state === 3 ? "#e8ff2a" : "#f7ffa6";
      } else if (severity === "warning") {
        backgroundColor = state === 1 || state === 3 ? "#ff8b00" : "#ffd7a6";
      }

      return {
        backgroundColor: isSelected ? "#c0c0c0" : backgroundColor,
        cursor: "pointer",
      };
    };

    //========================================================================
    // Weitere Alarme laden
    const loadMoreAlarms = () => {
      fetchNextArchivedAlarms(archivedAlarmsOffset)
        .then((result) => {
          if (result.alarms.length > 0) {
            // Neue Alarme an bestehende Liste anhängen
            const combinedAlarms = [...archivedAlarms, ...result.alarms];

            // Liste neu sortieren entsprechend der aktuellen Sortierkonfiguration
            let sortedAlarms = [...combinedAlarms];
            if (sortConfig.key !== null) {
              sortedAlarms.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) {
                  return sortConfig.direction === "ascending" ? -1 : 1;
                }
                if (a[sortConfig.key] > b[sortConfig.key]) {
                  return sortConfig.direction === "ascending" ? 1 : -1;
                }
                return 0;
              });
            }

            setArchivedAlarms(sortedAlarms);
            setArchivedAlarmsOffset(result.nextOffset);
            setHasMoreArchivedAlarms(result.hasMoreArchivedAlarms);
          }
        })
        .catch((error) => {
          // console.error("Error loading more alarms:", error);
        });
    };

    //========================================================================
    // Tabelle sortieren
    const sortedAlarms = React.useMemo(() => {
      let sortableAlarms = activeAlarms
        ? [...initialAlarms]
        : [...archivedAlarms];
      if (sortConfig.key !== null) {
        sortableAlarms.sort((a, b) => {
          if (a[sortConfig.key] < b[sortConfig.key]) {
            return sortConfig.direction === "ascending" ? -1 : 1;
          }
          if (a[sortConfig.key] > b[sortConfig.key]) {
            return sortConfig.direction === "ascending" ? 1 : -1;
          }
          return 0;
        });
      }
      return sortableAlarms;
    }, [initialAlarms, archivedAlarms, activeAlarms, sortConfig]);

    const requestSort = (key) => {
      let direction = "ascending";
      if (sortConfig.key === key && sortConfig.direction === "ascending") {
        direction = "descending";
      }
      setSortConfig({ key, direction });
    };

    //========================================================================
    // Zeile auswählen
    const toggleRowSelection = (index) => {
      setSelectedRows((prevSelectedRows) =>
        prevSelectedRows.includes(index)
          ? prevSelectedRows.filter((i) => i !== index)
          : [...prevSelectedRows, index],
      );
    };

    //========================================================================
    // Alarme quittieren
    const quittAlarm = (quittMode) => {
      if (quittMode === "quittAll") {
        const action = activeAlarms
          ? "quittAllActAlarms"
          : "quittAllArchAlarms";
        websocket.quittAlarm(globalVars.ws, action);
      } else if (quittMode === "quittOne") {
        let selectedIndexes = [];

        selectedRows.forEach((selectedIndex) => {
          const alarm = sortedAlarms[selectedIndex];
          if (alarm) {
            const identifier = activeAlarms ? alarm.id : alarm.idx;
            selectedIndexes.push(identifier);
          }
        });

        if (selectedIndexes.length > 0) {
          const action = activeAlarms ? "quittActAlarm" : "quittArchAlarm";
          websocket.quittAlarm(globalVars.ws, action, selectedIndexes);
        }
      }
      setSelectedRows([]);
    };

    //========================================================================
    // Spaltenbreite festlegen
    const commonStyles = {
      textAlign: "center",
      border: "1px solid #ddd",
    };

    const columnStyles = {
      severity: { width: "8%", ...commonStyles },
      text: { ...commonStyles, textAlign: "left", paddingLeft: "10px" },
      raised: { width: "15%", ...commonStyles },
      cleared: { width: "15%", ...commonStyles },
      confirmed: { width: "15%", ...commonStyles },
    };

    //========================================================================
    // wenn aktive Alarme, Text-Spalte breiter, da keine Uhrzeit Alarm gegangen
    if (activeAlarms) {
      columnStyles.text.width = "62%";
    } else {
      columnStyles.text.width = "47%";
    }

    return (
      <div style={{ ...style }}>
        <div
          className="scroll-container"
          style={{
            position: "absolute",
            width: "100%",
            height: `calc(100% - (${scaleFactor} * 40px))`,
            top: "0px",
            overflowY: "scroll",
            overflowX: "auto",
            scrollbarWidth: "none",
            backgroundColor: "lightgrey",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            {/* ######################################################################################################## */}
            {/* ################################################ Alarme ################################################ */}
            <thead
              style={{
                position: "sticky",
                top: -1,
                zIndex: 1,
                cursor: "pointer",
                backgroundColor: "#636363",
                fontWeight: "bold",
                userSelect: "none",
              }}
            >
              <tr>
                <th
                  style={columnStyles.severity}
                  onClick={() => requestSort("severity")}
                >
                  {language.displayText(["Typ", "tipo", "type"])}
                </th>
                <th
                  style={columnStyles.text}
                  onClick={() => requestSort("text")}
                >
                  {language.displayText([
                    "Alarmtext",
                    "testo allarme",
                    "alarmtext",
                  ])}
                </th>
                <th
                  style={columnStyles.raised}
                  onClick={() => requestSort("raised")}
                >
                  {language.displayText(["Aktiv", "attivo", "raised"])}
                </th>
                {!activeAlarms && (
                  <th
                    style={columnStyles.cleared}
                    onClick={() => requestSort("cleared")}
                  >
                    {language.displayText(["Deaktiv", "disattivo", "cleared"])}
                  </th>
                )}
                <th
                  style={columnStyles.confirmed}
                  onClick={() => requestSort("confirmed")}
                >
                  {language.displayText([
                    "Quittiert",
                    "confermato",
                    "confirmed",
                  ])}
                </th>
              </tr>
            </thead>
            {/* ######################################################################################################## */}
            {/* ################################################ Alarme ################################################ */}
            <tbody>
              {sortedAlarms.map((alarm, index) => (
                <tr
                  key={index}
                  style={{
                    ...getRowColor(
                      alarm.severity,
                      alarm.state,
                      selectedRows.includes(index),
                    ),
                    height: `calc(${scaleFactor} * 35px)`,
                  }}
                  onClick={() => toggleRowSelection(index)}
                >
                  <td style={columnStyles.severity}>
                    {alarmSeverity(alarm.severity)}
                  </td>
                  <td style={columnStyles.text}>
                    {language.displayText(alarm.text)}
                  </td>
                  <td style={columnStyles.raised}>
                    {formatDate(alarm.raised)}
                  </td>
                  {!activeAlarms && (
                    <td style={columnStyles.cleared}>
                      {formatDate(alarm.cleared)}
                    </td>
                  )}
                  <td style={columnStyles.confirmed}>
                    {formatDate(alarm.confirmed)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button
          style={{
            position: "absolute",
            bottom: "0px",
            left: `calc(${scaleFactor} * 0px)`,
            height: `calc(${scaleFactor} * 35px)`,
            width: `calc(${scaleFactor} * 80px)`,
            textAlign: "center",
            fontSize: `calc(${style.fontSize} * 0.8)`,
          }}
          onClick={() => quittAlarm("quittOne")}
        >
          {language.displayText(["Quittieren", "confermare", "confirm"])}
        </button>
        <button
          style={{
            position: "absolute",
            bottom: "0px",
            left: `calc(${scaleFactor} * 90px)`,
            height: `calc(${scaleFactor} * 35px)`,
            width: `calc(${scaleFactor} * 80px)`,
            textAlign: "center",
            fontSize: `calc(${style.fontSize} * 0.8)`,
          }}
          onClick={() => quittAlarm("quittAll")}
        >
          {language.displayText(["Quitt. alle", "conf. tutti", "conf. all"])}
        </button>

        {!activeAlarms && (
          <div>
            {hasMoreArchivedAlarms && (
              <button
                style={{
                  position: "absolute",
                  bottom: "0px",
                  right: `calc(${scaleFactor} * 0px)`,
                  height: `calc(${scaleFactor} * 35px)`,
                  width: `calc(${scaleFactor} * 180px)`,
                  textAlign: "center",
                  fontSize: `calc(${style.fontSize} * 0.8)`,
                }}
                onClick={loadMoreAlarms}
              >
                {language.displayText([
                  "weitere Alarme \nladen",
                  "load \nmore",
                  "load \nmore",
                ])}
              </button>
            )}
          </div>
        )}
      </div>
    );
  },
);

export default TableComponentMobile;
